(() => {
  const root = document.documentElement;
  root.classList.add('sb-js');

  const reveal = (scope = document) => {
    const elements = [...scope.querySelectorAll('.sb-reveal:not([data-sb-observed])')];
    if (!elements.length) return;

    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      elements.forEach((element) => element.classList.add('sb-is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries, currentObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('sb-is-visible');
        currentObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

    elements.forEach((element) => {
      element.dataset.sbObserved = 'true';
      observer.observe(element);
    });
  };

  const renderCountdown = (element) => {
    const output = element.querySelector('[data-sb-countdown-output]');
    if (!output) return;

    const rawDeadline = element.dataset.sbCountdown;
    const deadline = rawDeadline ? new Date(rawDeadline) : null;
    if (!deadline || Number.isNaN(deadline.getTime())) {
      output.textContent = output.dataset.sbFallback || 'Limited run';
      return;
    }

    const tick = () => {
      const remaining = deadline.getTime() - Date.now();
      if (remaining <= 0) {
        output.textContent = output.dataset.sbEnded || 'Drop closed';
        return false;
      }

      const days = Math.floor(remaining / 86400000);
      const hours = Math.floor((remaining % 86400000) / 3600000);
      const minutes = Math.floor((remaining % 3600000) / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      output.textContent = days > 0
        ? `${days}d ${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`
        : `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      return true;
    };

    if (tick()) {
      const interval = window.setInterval(() => {
        if (!document.body.contains(element) || !tick()) window.clearInterval(interval);
      }, 1000);
    }
  };

  const countdowns = (scope = document) => {
    scope.querySelectorAll('[data-sb-countdown]:not([data-sb-countdown-ready])').forEach((element) => {
      element.dataset.sbCountdownReady = 'true';
      renderCountdown(element);
    });
  };

  const clamp = (value, minimum = 0, maximum = 1) => Math.min(maximum, Math.max(minimum, value));

  const renderFlower = (element) => {
    const lifecycle = element.dataset.sbLifecycle || 'live';
    const enrolled = element.dataset.sbEnrolled === 'true';
    const revivalCount = Math.max(0, Number(element.dataset.sbRevivalCount) || 0);
    const revivalThreshold = Math.max(1, Number(element.dataset.sbRevivalThreshold) || 50);
    const bloomCycle = Math.max(1, Number(element.dataset.sbBloomCycle) || 1);
    const title = element.querySelector('[data-sb-flower-title]');
    const copy = element.querySelector('[data-sb-flower-copy]');
    const progress = element.querySelector('[data-sb-flower-progress]');
    const meter = element.querySelector('[data-sb-flower-meter]');
    const isQueued = lifecycle === 'queued';
    const isReviving = lifecycle === 'archived' || lifecycle === 'reviving';
    const isFinalArchive = lifecycle === 'final_archive';
    const isUnavailable = lifecycle === 'unavailable';
    let health = 1;
    let meterMaximum = 100;
    let meterValue = 100;

    if (!enrolled || isUnavailable) {
      health = 0;
      meterValue = 0;
      if (title) title.textContent = 'Bloom unavailable';
      if (copy) copy.textContent = 'This release is temporarily unavailable while its lifecycle state is reconciled.';
      if (progress) progress.textContent = 'Public shopping is paused';
    } else if (isFinalArchive) {
      health = 0;
      meterValue = 0;
      if (title) title.textContent = 'Final archive';
      if (copy) copy.textContent = 'This release completed both blooms and now remains in the archive.';
      if (progress) progress.textContent = 'Two bloom cycles complete';
    } else if (isQueued) {
      const startsAt = element.dataset.sbLaunchedAt ? new Date(element.dataset.sbLaunchedAt) : null;
      const startIsValid = startsAt && !Number.isNaN(startsAt.getTime());
      health = 0.14;
      meterValue = 14;
      if (title) title.textContent = bloomCycle > 1 ? 'Second bloom queued' : 'Bloom queued';
      if (copy) {
        copy.textContent = bloomCycle > 1
          ? 'The community brought this release back. Its final fourteen-day window is on the calendar.'
          : 'This complete graphic world is finished and waiting for its scheduled release.';
      }
      if (progress) {
        if (startIsValid) {
          const remaining = startsAt.getTime() - Date.now();
          if (remaining <= 0) {
            progress.textContent = 'Scheduled start reached · opening now';
          } else {
            const hours = Math.ceil(remaining / 3600000);
            const days = Math.floor(hours / 24);
            const leftoverHours = hours % 24;
            progress.textContent = days > 0
              ? `Blooms in ${days}d ${leftoverHours}h`
              : `Blooms in ${hours}h`;
          }
        } else {
          progress.textContent = 'Scheduled release pending';
        }
      }
    } else if (isReviving) {
      const requestRatio = clamp(revivalCount / revivalThreshold);
      health = requestRatio * 0.88;
      meterMaximum = revivalThreshold;
      meterValue = Math.min(revivalCount, revivalThreshold);
      if (title) {
        title.textContent = requestRatio >= 1 ? 'Second bloom pending' : 'Revival in progress';
      }
      if (copy) {
        copy.textContent = requestRatio >= 1
          ? 'The threshold is met. The final fourteen-day run is being prepared.'
          : 'Each unique email request brings this flower closer to a second bloom.';
      }
      if (progress) progress.textContent = `${revivalCount} of ${revivalThreshold} unique requests`;
    } else {
      const launchedAt = element.dataset.sbLaunchedAt ? new Date(element.dataset.sbLaunchedAt) : null;
      const expiresAt = element.dataset.sbExpiresAt ? new Date(element.dataset.sbExpiresAt) : null;
      const datesAreValid = launchedAt
        && expiresAt
        && !Number.isNaN(launchedAt.getTime())
        && !Number.isNaN(expiresAt.getTime())
        && expiresAt.getTime() > launchedAt.getTime();

      if (datesAreValid) {
        const now = Date.now();
        const totalWindow = expiresAt.getTime() - launchedAt.getTime();
        const remaining = expiresAt.getTime() - now;
        health = clamp(remaining / totalWindow);
        meterValue = Math.round(health * 100);

        if (remaining <= 0) {
          health = 0;
          meterValue = 0;
          if (title) title.textContent = 'Bloom window complete';
          if (copy) copy.textContent = 'This release is leaving the public shop.';
          if (progress) progress.textContent = 'Awaiting archive rotation';
        } else {
          const elapsed = Math.max(0, now - launchedAt.getTime());
          const day = Math.min(14, Math.floor(elapsed / 86400000) + 1);
          const daysLeft = Math.max(1, Math.ceil(remaining / 86400000));
          if (title) {
            title.textContent = bloomCycle > 1
              ? `Second bloom · day ${day} of 14`
              : `Bloom day ${day} of 14`;
          }
          if (copy) copy.textContent = 'The flower fades with the real public release window.';
          if (progress) progress.textContent = `${daysLeft} day${daysLeft === 1 ? '' : 's'} left in the public shop`;
        }
      } else {
        if (title) title.textContent = bloomCycle > 1 ? 'Second bloom active' : 'Fourteen days in bloom';
        if (progress) progress.textContent = 'Fourteen-day public window';
      }
    }

    element.style.setProperty('--sb-bloom-health', health.toFixed(3));
    element.style.setProperty('--sb-bloom-progress', `${Math.round((isReviving ? clamp(revivalCount / revivalThreshold) : health) * 100)}%`);
    if (meter) {
      meter.setAttribute('aria-valuemax', String(meterMaximum));
      meter.setAttribute('aria-valuenow', String(meterValue));
    }
  };

  const flowers = (scope = document) => {
    scope.querySelectorAll('[data-sb-flower]:not([data-sb-flower-ready])').forEach((element) => {
      element.dataset.sbFlowerReady = 'true';
      renderFlower(element);
      const interval = window.setInterval(() => {
        if (!document.body.contains(element)) {
          window.clearInterval(interval);
          return;
        }
        renderFlower(element);
      }, 60000);
    });
  };

  const revivalSuccesses = (scope = document) => {
    scope.querySelectorAll('[data-sb-revival-success]:not([data-sb-revival-success-ready])').forEach((success) => {
      success.dataset.sbRevivalSuccessReady = 'true';
      const drop = success.closest('.jhd-numbered-drop');
      if (!drop) return;
      drop.querySelectorAll('[data-sb-flower]').forEach((flower) => {
        const threshold = Math.max(1, Number(flower.dataset.sbRevivalThreshold) || 50);
        const current = Math.max(0, Number(flower.dataset.sbRevivalCount) || 0);
        flower.dataset.sbRevivalCount = String(Math.min(threshold, current + 1));
        flower.classList.add('is-request-received');
        renderFlower(flower);
        const progress = flower.querySelector('[data-sb-flower-progress]');
        if (progress) progress.textContent = 'Request received · public meter updates after unique-email checks';
      });
    });
  };

  const init = (scope = document) => {
    reveal(scope);
    countdowns(scope);
    flowers(scope);
    revivalSuccesses(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
